import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '@/utils/axios';
import { toast } from 'sonner';

// Initial state
const initialState = {
  // Payment initiation
  initiatingPayment: false,
  paymentInitError: null,
  currentPayment: null, // { reference, authorizationUrl, gateway, amount, etc. }
  
  // Payment verification
  verifyingPayment: false,
  verificationError: null,
  verificationResult: null,
  
  // Payment history/transactions
  transactions: [],
  transactionsLoading: false,
  transactionsError: null,
  
  // Selected gateway
  selectedGateway: 'paystack', // 'paystack' | 'flutterwave'
  
  // Gateway statuses
  gatewayStatuses: {
    paystack: { enabled: true },
    flutterwave: { enabled: true },
  },
  gatewayStatusesLoading: false,
  gatewayStatusesError: null,
};

// Async thunks

// Unified payment initiation
export const initiatePayment = createAsyncThunk(
  'payment/initiatePayment',
  async ({ gateway, email, amount, metadata = {}, callbackUrl, idempotencyKey }, { rejectWithValue }) => {
    try {
      // Ensure amount is a valid number
      const numericAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
      
      if (isNaN(numericAmount) || numericAmount <= 0) {
        toast.error('Invalid payment amount');
        return rejectWithValue('Invalid payment amount');
      }

      const response = await axios.post(`/payment/${gateway}/initiate`, {
        email,
        amount: numericAmount,
        metadata,
        callbackUrl,
        idempotencyKey,
      });
      
      return {
        ...response.data.data || response.data,
        gateway,
      };
    } catch (error) {
      // Extract validation error details if available
      const validationDetails = error.response?.data?.error?.details;
      let message = error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        `Failed to initiate ${gateway} payment. Please try again.`;
      
      // If there are validation details, show the first one
      if (validationDetails && Array.isArray(validationDetails) && validationDetails.length > 0) {
        const firstError = validationDetails[0];
        message = `${firstError.field}: ${firstError.message}`;
      }
      
      console.error('Payment initiation error:', {
        status: error.response?.status,
        data: error.response?.data,
        validationDetails,
      });
      
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Paystack-specific initiation (for backward compatibility)
export const initiatePaystackPayment = createAsyncThunk(
  'payment/initiatePaystackPayment',
  async ({ email, amount }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/payment/paystack/initiate', { email, amount });
      return {
        ...response.data.data || response.data,
        gateway: 'paystack',
      };
    } catch (error) {
      const message =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        'Failed to initiate Paystack payment. Please try again.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Flutterwave-specific initiation (for backward compatibility)
export const initiateFlutterwavePayment = createAsyncThunk(
  'payment/initiateFlutterwavePayment',
  async ({ email, amount }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/payment/flutterwave/initiate', { email, amount });
      return {
        ...response.data.data || response.data,
        gateway: 'flutterwave',
      };
    } catch (error) {
      const message =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        'Failed to initiate Flutterwave payment. Please try again.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Paystack verification
export const verifyPaystackPayment = createAsyncThunk(
  'payment/verifyPaystackPayment',
  async (reference, { rejectWithValue }) => {
    try {
      if (!reference || typeof reference !== 'string' || reference.trim().length === 0) {
        return rejectWithValue('Payment reference is required');
      }
      
      const response = await axios.get(`/payment/paystack/verify/${reference.trim()}`);
      
      if (response.data.success && response.data.data) {
        return {
          ...response.data.data,
          gateway: 'paystack',
          reference,
        };
      }
      
      return rejectWithValue(response.data.error || response.data.message || 'Payment verification failed');
    } catch (error) {
      const message =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        'Failed to verify Paystack payment.';
      return rejectWithValue(message);
    }
  }
);

// Flutterwave verification
export const verifyFlutterwavePayment = createAsyncThunk(
  'payment/verifyFlutterwavePayment',
  async (transactionId, { rejectWithValue }) => {
    try {
      if (!transactionId || typeof transactionId !== 'string' || transactionId.trim().length === 0) {
        return rejectWithValue('Transaction ID is required');
      }
      
      const response = await axios.get(`/payment/flutterwave/verify/${transactionId.trim()}`);
      
      if (response.data.success && response.data.data) {
        return {
          ...response.data.data,
          gateway: 'flutterwave',
          transactionId,
        };
      }
      
      return rejectWithValue(response.data.error || response.data.message || 'Payment verification failed');
    } catch (error) {
      const message =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        'Failed to verify Flutterwave payment.';
      return rejectWithValue(message);
    }
  }
);

// Unified verification (tries to determine gateway from reference/transactionId)
export const verifyPayment = createAsyncThunk(
  'payment/verifyPayment',
  async ({ reference, transactionId, gateway }, { rejectWithValue, dispatch }) => {
    try {
      if (gateway === 'paystack' || reference) {
        return await dispatch(verifyPaystackPayment(reference || transactionId)).unwrap();
      } else if (gateway === 'flutterwave' || transactionId) {
        return await dispatch(verifyFlutterwavePayment(transactionId || reference)).unwrap();
      }
      
      return rejectWithValue('Either reference or transactionId with gateway must be provided');
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Fetch payment gateway statuses
export const fetchPaymentGatewaysStatus = createAsyncThunk(
  'payment/fetchPaymentGatewaysStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/payment/gateways/status');
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      return rejectWithValue(response.data.error || 'Failed to fetch payment gateway statuses');
    } catch (error) {
      const message =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        'Failed to fetch payment gateway statuses';
      return rejectWithValue(message);
    }
  }
);

// Payment slice
const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    setSelectedGateway: (state, action) => {
      state.selectedGateway = action.payload;
    },
    clearCurrentPayment: (state) => {
      state.currentPayment = null;
      state.paymentInitError = null;
    },
    clearVerificationResult: (state) => {
      state.verificationResult = null;
      state.verificationError = null;
    },
    clearPaymentState: (state) => {
      state.currentPayment = null;
      state.paymentInitError = null;
      state.verificationResult = null;
      state.verificationError = null;
    },
  },
  extraReducers: (builder) => {
    // Unified payment initiation
    builder
      .addCase(initiatePayment.pending, (state) => {
        state.initiatingPayment = true;
        state.paymentInitError = null;
      })
      .addCase(initiatePayment.fulfilled, (state, action) => {
        state.initiatingPayment = false;
        state.currentPayment = action.payload;
        state.paymentInitError = null;
      })
      .addCase(initiatePayment.rejected, (state, action) => {
        state.initiatingPayment = false;
        state.paymentInitError = action.payload;
        state.currentPayment = null;
      });

    // Paystack initiation
    builder
      .addCase(initiatePaystackPayment.pending, (state) => {
        state.initiatingPayment = true;
        state.paymentInitError = null;
      })
      .addCase(initiatePaystackPayment.fulfilled, (state, action) => {
        state.initiatingPayment = false;
        state.currentPayment = action.payload;
        state.paymentInitError = null;
      })
      .addCase(initiatePaystackPayment.rejected, (state, action) => {
        state.initiatingPayment = false;
        state.paymentInitError = action.payload;
        state.currentPayment = null;
      });

    // Flutterwave initiation
    builder
      .addCase(initiateFlutterwavePayment.pending, (state) => {
        state.initiatingPayment = true;
        state.paymentInitError = null;
      })
      .addCase(initiateFlutterwavePayment.fulfilled, (state, action) => {
        state.initiatingPayment = false;
        state.currentPayment = action.payload;
        state.paymentInitError = null;
      })
      .addCase(initiateFlutterwavePayment.rejected, (state, action) => {
        state.initiatingPayment = false;
        state.paymentInitError = action.payload;
        state.currentPayment = null;
      });

    // Paystack verification
    builder
      .addCase(verifyPaystackPayment.pending, (state) => {
        state.verifyingPayment = true;
        state.verificationError = null;
      })
      .addCase(verifyPaystackPayment.fulfilled, (state, action) => {
        state.verifyingPayment = false;
        state.verificationResult = action.payload;
        state.verificationError = null;
      })
      .addCase(verifyPaystackPayment.rejected, (state, action) => {
        state.verifyingPayment = false;
        state.verificationError = action.payload;
        state.verificationResult = null;
      });

    // Flutterwave verification
    builder
      .addCase(verifyFlutterwavePayment.pending, (state) => {
        state.verifyingPayment = true;
        state.verificationError = null;
      })
      .addCase(verifyFlutterwavePayment.fulfilled, (state, action) => {
        state.verifyingPayment = false;
        state.verificationResult = action.payload;
        state.verificationError = null;
      })
      .addCase(verifyFlutterwavePayment.rejected, (state, action) => {
        state.verifyingPayment = false;
        state.verificationError = action.payload;
        state.verificationResult = null;
      });

    // Unified verification
    builder
      .addCase(verifyPayment.pending, (state) => {
        state.verifyingPayment = true;
        state.verificationError = null;
      })
      .addCase(verifyPayment.fulfilled, (state, action) => {
        state.verifyingPayment = false;
        state.verificationResult = action.payload;
        state.verificationError = null;
      })
      .addCase(verifyPayment.rejected, (state, action) => {
        state.verifyingPayment = false;
        state.verificationError = action.payload;
        state.verificationResult = null;
      });

    // Fetch payment gateway statuses
    builder
      .addCase(fetchPaymentGatewaysStatus.pending, (state) => {
        state.gatewayStatusesLoading = true;
        state.gatewayStatusesError = null;
      })
      .addCase(fetchPaymentGatewaysStatus.fulfilled, (state, action) => {
        state.gatewayStatusesLoading = false;
        state.gatewayStatuses = action.payload;
        state.gatewayStatusesError = null;
      })
      .addCase(fetchPaymentGatewaysStatus.rejected, (state, action) => {
        state.gatewayStatusesLoading = false;
        state.gatewayStatusesError = action.payload;
        // Keep default values on error
      });
  },
});

export const {
  setSelectedGateway,
  clearCurrentPayment,
  clearVerificationResult,
  clearPaymentState,
} = paymentSlice.actions;

// Selectors
export const selectInitiatingPayment = (state) => state.payment.initiatingPayment;
export const selectPaymentInitError = (state) => state.payment.paymentInitError;
export const selectCurrentPayment = (state) => state.payment.currentPayment;

export const selectVerifyingPayment = (state) => state.payment.verifyingPayment;
export const selectVerificationError = (state) => state.payment.verificationError;
export const selectVerificationResult = (state) => state.payment.verificationResult;

export const selectSelectedGateway = (state) => state.payment.selectedGateway;

export const selectTransactions = (state) => state.payment.transactions;
export const selectTransactionsLoading = (state) => state.payment.transactionsLoading;
export const selectTransactionsError = (state) => state.payment.transactionsError;

export const selectGatewayStatuses = (state) => state.payment.gatewayStatuses;
export const selectGatewayStatusesLoading = (state) => state.payment.gatewayStatusesLoading;
export const selectGatewayStatusesError = (state) => state.payment.gatewayStatusesError;
export const selectPaystackEnabled = (state) => state.payment.gatewayStatuses.paystack?.enabled ?? true;
export const selectFlutterwaveEnabled = (state) => state.payment.gatewayStatuses.flutterwave?.enabled ?? true;

export default paymentSlice.reducer;

